import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
	createSuggestionDraft,
	NUDGE_MAX_SUGGESTIONS,
	NUDGE_PROMPT_MAX_LENGTH,
	NUDGE_SUGGESTION_MAX_LENGTH,
	type NudgeSuggestionDraft
} from '../nudge-draft';
import { ADD_BUTTON, PANEL_CARD, PANEL_TEXTAREA } from '../ui-chrome';

interface NudgeMessageEditorProps {
	prompt: string;
	suggestions: NudgeSuggestionDraft[];
	editable: boolean;
	error: string | null;
	onPromptChange: (prompt: string) => void;
	onSuggestionsChange: (suggestions: NudgeSuggestionDraft[]) => void;
}

/**
 * The "What to nudge" card: the message shoppers see, shown word for word, and up to three questions
 * the shopper can tap. There is always at least one question box; an empty one is simply not saved.
 */
export const NudgeMessageEditor = ({
	prompt,
	suggestions,
	editable,
	error,
	onPromptChange,
	onSuggestionsChange
}: NudgeMessageEditorProps) => {
	const setSuggestion = (id: string, text: string) =>
		onSuggestionsChange(suggestions.map((suggestion) => (suggestion.id === id ? { ...suggestion, text } : suggestion)));
	const removeSuggestion = (id: string) => onSuggestionsChange(suggestions.filter((suggestion) => suggestion.id !== id));

	return (
		<section className={PANEL_CARD} aria-labelledby='nudge-what-title'>
			<h3 id='nudge-what-title' className='text-sm leading-5 font-semibold text-ink'>
				What to nudge
			</h3>
			<p className='text-xs leading-4 text-gray-800'>
				Write the message exactly as shoppers should see it. It is shown word for word.
			</p>
			<Textarea
				value={prompt}
				disabled={!editable}
				maxLength={NUDGE_PROMPT_MAX_LENGTH}
				onChange={(event) => onPromptChange(event.target.value)}
				placeholder='e.g. Still deciding? I can help you pick the right size.'
				aria-label='What to nudge'
				className={cn(PANEL_TEXTAREA, 'mt-4')}
			/>

			<h4 className='mt-6 text-sm leading-5 font-semibold text-ink'>Pre-defined suggestion</h4>
			<div className='mt-2 flex flex-col gap-3'>
				{suggestions.map((suggestion, index) => (
					<div key={suggestion.id} className='flex flex-col gap-2'>
						<div className='flex h-4 items-center justify-between'>
							<label htmlFor={suggestion.id} className='text-xs text-gray-900'>
								Question {index + 1}
							</label>
							{editable && suggestions.length > 1 && (
								<Button
									type='button'
									variant='ghost'
									size='icon-sm'
									onClick={() => removeSuggestion(suggestion.id)}
									aria-label={`Remove question ${index + 1}`}
									className='size-5 text-gray-800'
								>
									<X className='size-3.5' />
								</Button>
							)}
						</div>
						<Textarea
							id={suggestion.id}
							value={suggestion.text}
							disabled={!editable}
							maxLength={NUDGE_SUGGESTION_MAX_LENGTH}
							onChange={(event) => setSuggestion(suggestion.id, event.target.value)}
							placeholder='Type Something...'
							className={PANEL_TEXTAREA}
						/>
					</div>
				))}
			</div>

			{error && (
				<p role='alert' className='mt-3 text-xs text-red-600'>
					{error}
				</p>
			)}

			{suggestions.length < NUDGE_MAX_SUGGESTIONS && (
				<Button
					type='button'
					variant='outline'
					disabled={!editable}
					onClick={() => onSuggestionsChange([...suggestions, createSuggestionDraft()])}
					className={`${ADD_BUTTON} mt-4`}
				>
					<Plus className='size-3.5' /> Add question
				</Button>
			)}
		</section>
	);
};
